import React, { useState } from 'react';
import { Check, X, Users, MessageSquare, Zap, Phone } from 'lucide-react';
import {
  useAdminUsers,
  useApproveUser,
  useDenyUser,
  useAdminStats,
  usePlans,
  useCreatePlan,
  useUpdatePlan,
  useDeletePlan,
  useChangeUserPlan,
} from '../lib/hooks';

interface AdminUser {
  id: string;
  email: string;
  phoneNumber?: string;
  phoneVerified: boolean;
  createdAt: string;
  usageQuota: number;
  subscriptionTier: string;
  isAdmin: boolean;
  approvalStatus: string;
  planId?: string;
}

interface Plan {
  id: string;
  name: string;
  description?: string;
  tokens: number;
  price_monthly: number;
  features?: string[];
  is_active: boolean;
}

interface UsersResponse {
  users: AdminUser[];
  pagination?: { total: number };
}

interface SystemStats {
  total_users: number;
  approved_users: number;
  pending_users: number;
  denied_users: number;
  admin_users: number;
  verified_phones: number;
  total_messages: number;
  inbound_messages: number;
  outbound_messages: number;
  total_tokens_used: number;
}

const STATUS_TABS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'approved', label: 'Approved' },
  { value: 'waiting_approval', label: 'Pending' },
  { value: 'denied', label: 'Denied' },
];

const statusBadgeClass = (status: string): string => {
  switch (status) {
    case 'approved':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'waiting_approval':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'denied':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const statusLabel = (status: string): string => {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'waiting_approval':
      return 'Pending';
    case 'denied':
      return 'Denied';
    default:
      return status;
  }
};

const formatDate = (value?: string): string => {
  if (!value) return 'No date';
  const date = new Date(value);
  if (isNaN(date.getTime())) return 'Invalid date';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const inputClass =
  'block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-gray-500 focus:ring-gray-500';

const StatCard: React.FC<{ title: string; value: React.ReactNode; subtitle?: string; icon: any }> = ({
  title,
  value,
  subtitle,
  icon: Icon,
}) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</p>
        <p className="mt-2 text-3xl font-semibold text-gray-900">{value}</p>
        {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
      </div>
      <div className="ml-4 p-3 bg-gray-50 rounded-lg">
        <Icon className="w-6 h-6 text-gray-600" />
      </div>
    </div>
  </div>
);

interface PlanDraft {
  name: string;
  tokens: string;
  price_monthly: string;
}

const AdminPanel: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [planSelections, setPlanSelections] = useState<Record<string, string>>({});
  const [changePlanSelections, setChangePlanSelections] = useState<Record<string, string>>({});
  const [pageError, setPageError] = useState<string | null>(null);

  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [planDraft, setPlanDraft] = useState<PlanDraft>({ name: '', tokens: '', price_monthly: '' });
  const [showNewPlanForm, setShowNewPlanForm] = useState(false);
  const [newPlan, setNewPlan] = useState<PlanDraft>({ name: '', tokens: '', price_monthly: '' });

  const { data: pendingData } = useAdminUsers('waiting_approval') as { data?: UsersResponse };
  const { data: usersData, isLoading: usersLoading } = useAdminUsers(statusFilter) as {
    data?: UsersResponse;
    isLoading: boolean;
  };
  const { data: stats } = useAdminStats() as { data?: SystemStats };
  const { data: plansData } = usePlans() as { data?: Plan[] };

  const approveUser = useApproveUser() as any;
  const denyUser = useDenyUser() as any;
  const createPlan = useCreatePlan() as any;
  const updatePlan = useUpdatePlan() as any;
  const deletePlan = useDeletePlan() as any;
  const changeUserPlan = useChangeUserPlan() as any;

  const pendingUsers = pendingData?.users || [];
  const users = usersData?.users || [];
  const plans = plansData || [];

  const isApproving = (userId: string) =>
    Boolean(approveUser.isLoading && approveUser.variables?.userId === userId);
  const isDenying = (userId: string) =>
    Boolean(denyUser.isLoading && denyUser.variables === userId);

  const handleApprove = (userId: string) => {
    approveUser.mutate(
      { userId, planId: planSelections[userId] || undefined },
      { onError: () => setPageError('Failed to approve user') }
    );
  };

  const handleDeny = (userId: string) => {
    denyUser.mutate(userId, {
      onError: () => setPageError('Failed to deny user'),
    });
  };

  const handleChangePlan = (userId: string) => {
    const planId = changePlanSelections[userId];
    if (!planId) return;
    changeUserPlan.mutate(
      { userId, planId },
      { onError: () => setPageError('Failed to change plan') }
    );
  };

  const startEditPlan = (plan: Plan) => {
    setEditingPlanId(plan.id);
    setPlanDraft({
      name: plan.name,
      tokens: String(plan.tokens),
      price_monthly: String(plan.price_monthly),
    });
  };

  const cancelEditPlan = () => {
    setEditingPlanId(null);
  };

  const saveEditPlan = (planId: string) => {
    updatePlan.mutate(
      {
        planId,
        updates: {
          name: planDraft.name,
          tokens: Number(planDraft.tokens),
          price_monthly: Number(planDraft.price_monthly),
        },
      },
      {
        onSuccess: () => setEditingPlanId(null),
        onError: () => setPageError('Failed to update plan'),
      }
    );
  };

  const handleDeletePlan = (planId: string) => {
    deletePlan.mutate(planId, {
      onError: () => setPageError('Failed to delete plan'),
    });
  };

  const handleCreatePlan = () => {
    if (!newPlan.name || !newPlan.tokens) return;
    createPlan.mutate(
      {
        name: newPlan.name,
        tokens: Number(newPlan.tokens),
        price_monthly: Number(newPlan.price_monthly) || 0,
      },
      {
        onSuccess: () => {
          setNewPlan({ name: '', tokens: '', price_monthly: '' });
          setShowNewPlanForm(false);
        },
        onError: () => setPageError('Failed to create plan'),
      }
    );
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Admin Panel</h2>

      {pageError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-sm text-red-700">{pageError}</p>
          <button
            type="button"
            onClick={() => setPageError(null)}
            className="text-red-700 text-sm font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Pending approval queue */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Pending Approval</h3>
          <span
            data-testid="pending-badge"
            className="ml-2 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800"
          >
            {pendingUsers.length}
          </span>
        </div>

        {pendingUsers.length === 0 ? (
          <p className="text-sm text-gray-500">No users awaiting approval.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {pendingUsers.map(user => (
              <div key={user.id} className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{user.email}</p>
                  <p className="text-sm text-gray-500">
                    {user.phoneNumber || 'No phone'} &middot; {formatDate(user.createdAt)}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    className={`${inputClass} w-48`}
                    value={planSelections[user.id] || ''}
                    onChange={e =>
                      setPlanSelections(prev => ({ ...prev, [user.id]: e.target.value }))
                    }
                  >
                    <option value="">Default plan</option>
                    {plans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} ({plan.tokens.toLocaleString()} tokens)
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    disabled={isApproving(user.id) || isDenying(user.id)}
                    onClick={() => handleApprove(user.id)}
                    className="inline-flex items-center px-3 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={isApproving(user.id) || isDenying(user.id)}
                    onClick={() => handleDeny(user.id)}
                    className="inline-flex items-center px-3 py-2 border border-red-300 text-red-600 text-sm font-medium rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Users"
            value={stats.total_users}
            subtitle={`${stats.approved_users} approved, ${stats.pending_users} pending`}
            icon={Users}
          />
          <StatCard
            title="Messages (30 days)"
            value={stats.total_messages}
            subtitle={`${stats.inbound_messages} in, ${stats.outbound_messages} out`}
            icon={MessageSquare}
          />
          <StatCard
            title="Tokens Used (30 days)"
            value={(stats.total_tokens_used || 0).toLocaleString()}
            icon={Zap}
          />
          <StatCard
            title="Verified Phones"
            value={stats.verified_phones}
            subtitle={`${stats.admin_users} admin users`}
            icon={Phone}
          />
        </div>
      )}

      {/* Users table */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">User Management</h3>
          <div className="flex space-x-1">
            {STATUS_TABS.map(tab => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setStatusFilter(tab.value)}
                className={
                  statusFilter === tab.value
                    ? 'px-3 py-1.5 text-sm font-medium rounded-md bg-gray-900 text-white'
                    : 'px-3 py-1.5 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-100'
                }
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {usersLoading ? (
          <p className="text-sm text-gray-500 py-4">Loading...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Phone</th>
                  <th className="py-2 pr-4">Tier</th>
                  <th className="py-2 pr-4">Quota</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2 pr-4">Change Plan</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map(user => (
                  <tr key={user.id}>
                    <td className="py-2 pr-4 text-sm text-gray-900">
                      {user.email}
                      {user.isAdmin && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          Admin
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-sm text-gray-600">
                      {user.phoneNumber || 'Not set'}
                    </td>
                    <td className="py-2 pr-4 text-sm text-gray-600">
                      {user.subscriptionTier || 'No plan'}
                    </td>
                    <td className="py-2 pr-4 text-sm text-gray-600">
                      {user.usageQuota.toLocaleString()}
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        data-testid={`status-${user.id}`}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadgeClass(
                          user.approvalStatus
                        )}`}
                      >
                        {statusLabel(user.approvalStatus)}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-sm text-gray-600">{formatDate(user.createdAt)}</td>
                    <td className="py-2 pr-4">
                      {user.approvalStatus === 'approved' ? (
                        <div className="flex items-center space-x-2">
                          <select
                            aria-label={`change plan for ${user.email}`}
                            className={`${inputClass} w-40`}
                            value={changePlanSelections[user.id] ?? ''}
                            onChange={e =>
                              setChangePlanSelections(prev => ({
                                ...prev,
                                [user.id]: e.target.value,
                              }))
                            }
                          >
                            <option value="">Select plan</option>
                            {plans.map(plan => (
                              <option key={plan.id} value={plan.id}>
                                {plan.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => handleChangePlan(user.id)}
                            disabled={!changePlanSelections[user.id] || changeUserPlan.isLoading}
                            className="text-sm font-medium text-gray-900 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Apply
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <p className="text-sm text-gray-500 py-4">No users found.</p>
            )}
          </div>
        )}
      </div>

      {/* Plans */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Plans</h3>
          <button
            type="button"
            onClick={() => setShowNewPlanForm(v => !v)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
          >
            {showNewPlanForm ? 'Cancel' : 'Add Plan'}
          </button>
        </div>

        {showNewPlanForm && (
          <div className="mb-4 p-4 bg-gray-50 rounded-md grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
              <input
                className={inputClass}
                value={newPlan.name}
                onChange={e => setNewPlan(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Tokens</label>
              <input
                type="number"
                className={inputClass}
                value={newPlan.tokens}
                onChange={e => setNewPlan(prev => ({ ...prev, tokens: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Price / mo</label>
              <input
                type="number"
                className={inputClass}
                value={newPlan.price_monthly}
                onChange={e => setNewPlan(prev => ({ ...prev, price_monthly: e.target.value }))}
              />
            </div>
            <button
              type="button"
              onClick={handleCreatePlan}
              disabled={createPlan.isLoading}
              className="inline-flex items-center justify-center px-3 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 disabled:opacity-50"
            >
              Create
            </button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Tokens</th>
                <th className="py-2 pr-4">Price / mo</th>
                <th className="py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {plans.map(plan => {
                const editing = editingPlanId === plan.id;
                return (
                  <tr key={plan.id}>
                    <td className="py-2 pr-4">
                      {editing ? (
                        <input
                          className={inputClass}
                          value={planDraft.name}
                          onChange={e =>
                            setPlanDraft(prev => ({ ...prev, name: e.target.value }))
                          }
                        />
                      ) : (
                        <span className="text-sm text-gray-900">{plan.name}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      {editing ? (
                        <input
                          type="number"
                          className={`${inputClass} w-28`}
                          value={planDraft.tokens}
                          onChange={e =>
                            setPlanDraft(prev => ({ ...prev, tokens: e.target.value }))
                          }
                        />
                      ) : (
                        <span className="text-sm text-gray-600">
                          {plan.tokens.toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      {editing ? (
                        <input
                          type="number"
                          className={`${inputClass} w-24`}
                          value={planDraft.price_monthly}
                          onChange={e =>
                            setPlanDraft(prev => ({ ...prev, price_monthly: e.target.value }))
                          }
                        />
                      ) : (
                        <span className="text-sm text-gray-600">
                          ${plan.price_monthly}/mo
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      {editing ? (
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => saveEditPlan(plan.id)}
                            disabled={updatePlan.isLoading}
                            className="text-sm font-medium text-gray-900 hover:underline disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditPlan}
                            className="text-sm font-medium text-gray-500 hover:underline"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => startEditPlan(plan)}
                            className="text-sm font-medium text-gray-900 hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePlan(plan.id)}
                            disabled={deletePlan.isLoading}
                            className="text-sm font-medium text-red-600 hover:underline disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {plans.length === 0 && <p className="text-sm text-gray-500 py-4">No plans yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
